import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Upload, 
  Button, 
  Card, 
  Image, 
  Divider, 
  message,
  Alert,
  Modal
} from 'antd';
import { 
  DownloadOutlined, 
  FileImageOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { 
  convertImage, 
  ImageFormat, 
  ConversionOptions, 
  getConversionInfo,
  formatToMimeType
} from '../services/imageService';
import { useLanguage } from '../i18n/LanguageContext';

const { Title, Paragraph } = Typography;
const { Dragger } = Upload;

// Color palette for different conversion types
const colorPalette = [
  '#4caf50',  // Green
  '#2196f3',  // Blue
  '#ff9800',  // Orange
  '#9c27b0',  // Purple
  '#f44336',  // Red
  '#00bcd4',  // Cyan
  '#009688',  // Teal
  '#673ab7',  // Deep Purple
  '#3f51b5',  // Indigo
  '#8bc34a',  // Light Green
  '#cddc39',  // Lime
  '#ffeb3b',  // Yellow
  '#ffc107',  // Amber
  '#795548',  // Brown
  '#607d8b',  // Blue Grey
  '#e91e63',  // Pink
  '#ff5722',  // Deep Orange
];

// Get file extension for a format
const getExtension = (format: ImageFormat): string => {
  if (format === 'jpeg') return 'jpg';
  return format;
};

// Generate a color based on from and to formats
const getColorForConversion = (from: ImageFormat, to: ImageFormat): string => {
  const formats: ImageFormat[] = ['webp', 'jpeg', 'jpg', 'png', 'ico', 'bmp', 'apng'];
  const fromIndex = formats.indexOf(from);
  const toIndex = formats.indexOf(to);
  const combinedIndex = (fromIndex * formats.length + toIndex) % colorPalette.length;
  
  return colorPalette[combinedIndex];
};

const DetailPage: React.FC = () => {
  const { format } = useParams<{ format: string }>();
  const navigate = useNavigate();
  
  const [fromFormat, setFromFormat] = useState<ImageFormat>('webp');
  const [toFormat, setToFormat] = useState<ImageFormat>('jpeg');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [convertedImage, setConvertedImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [pageColor, setPageColor] = useState<string>('#4caf50');
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const { t } = useLanguage();

  useEffect(() => {
    if (format) {
      const parts = format.split('-to-');
      if (parts.length === 2) {
        const from = parts[0] as ImageFormat;
        const to = parts[1] as ImageFormat;
        setFromFormat(from);
        setToFormat(to);
        setPageColor(getColorForConversion(from, to));
      } else if (parts.length === 1) {
        setFromFormat(parts[0] as ImageFormat);
      }
    }
  }, [format]);

  const handleConvert = async () => {
    if (!imageFile) {
      message.error(t('detail.upload.required'));
      return;
    }

    setLoading(true);
    try {
      // Use default conversion options
      const options: ConversionOptions = { quality: 90 };
      const result = await convertImage(imageFile, toFormat, options);
      setConvertedImage(result);
      message.success(t('detail.convert.success').replace('{format}', toFormat.toUpperCase()));
      
      // Auto download the converted image
      downloadConvertedImage(result);
    } catch (error) {
      message.error(t('detail.convert.error'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const downloadConvertedImage = (imageData: string) => {
    const link = document.createElement('a');
    link.href = imageData;
    link.download = `converted-image.${getExtension(toFormat)}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const showUploadSuccessModal = (fileName: string) => {
    setUploadedFileName(fileName);
    setUploadModalVisible(true);
  };

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    accept: formatToMimeType[fromFormat],
    maxCount: 1,
    showUploadList: true,
    beforeUpload: (file) => {
      // Check if the file type matches the expected source format
      const expectedType = formatToMimeType[fromFormat];
      const fileType = file.type;
      
      // For JPEG/JPG files, we need to handle both image/jpeg and image/jpg
      if ((fromFormat === 'jpeg' || fromFormat === 'jpg') && fileType === 'image/jpeg') {
        setImageFile(file);
        // Show upload success modal
        showUploadSuccessModal(file.name);
        return false;
      }
      
      if (fileType !== expectedType) {
        message.error(`You can only upload ${fromFormat.toUpperCase()} files for this conversion!`);
        return Upload.LIST_IGNORE;
      }
      
      // Set the file for conversion and prevent actual upload
      setImageFile(file);
      // Show upload success modal
      showUploadSuccessModal(file.name);
      return false;
    },
    onChange: (info) => {
      // This is still needed for interface updates
      const { status } = info.file;
      if (status === 'removed') {
        setImageFile(null);
        setConvertedImage(null);
      }
    },
  };

  return (
    <div className="container">
      <Typography>
        <Title style={{ color: pageColor }}>
          Convert {fromFormat.toUpperCase()} to {toFormat.toUpperCase()}
        </Title>
        <Paragraph>
          {getConversionInfo(fromFormat, toFormat, t)}
        </Paragraph>
      </Typography>

      <Card 
        style={{ 
          borderTop: `2px solid ${pageColor}`,
          overflow: 'visible'
        }}
      >
        <Alert
          message={t('detail.upload.description').replace('{fromFormat}', fromFormat.toUpperCase()).replace('{toFormat}', toFormat.toUpperCase())}
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Title level={4} className="upload-title" style={{ color: pageColor }}>
          {t('detail.step1.title').replace('{format}', fromFormat.toUpperCase())}
        </Title>
        <div style={{ position: 'relative', marginTop: 24 }}>
          <Dragger 
            {...uploadProps} 
            className="upload-area"
            style={{ 
              borderColor: pageColor,
              borderWidth: '1px',
              borderStyle: 'dashed'
            }}
          >
            {imageFile ? (
              <div>
                <p className="ant-upload-drag-icon">
                  <CheckCircleOutlined style={{ color: pageColor, fontSize: 48 }} />
                </p>
                <p className="ant-upload-text" style={{ color: pageColor, fontWeight: 'bold' }}>
                  {t('detail.upload.complete').replace('{filename}', imageFile.name)}
                </p>
                <p className="ant-upload-hint">
                  {t('detail.upload.change')}
                </p>
              </div>
            ) : (
              <div>
                <p className="ant-upload-drag-icon">
                  <FileImageOutlined style={{ color: pageColor, fontSize: 48 }} />
                </p>
                <p className="ant-upload-text">{t('detail.upload.hint').replace('{format}', fromFormat.toUpperCase())}</p>
                <p className="ant-upload-hint">
                  {t('detail.upload.format.hint').replace('{format}', fromFormat.toUpperCase())}
                </p>
              </div>
            )}
          </Dragger>
        </div>

        <Divider />

        <Title level={4} className="upload-title" style={{ color: pageColor }}>
          {t('detail.step2.title')}
        </Title>
        
        <div className="button-group" style={{ display: 'flex', alignItems: 'center', marginTop: 24 }}>
          <Button 
            type="primary" 
            size="large" 
            onClick={handleConvert}
            loading={loading}
            disabled={!imageFile}
            icon={<ArrowRightOutlined />}
            style={{ 
              backgroundColor: pageColor,
              borderColor: pageColor,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {t('detail.convert.button')}
          </Button>

          <Button 
            size="large" 
            onClick={() => navigate('/')}
          >
            {t('compress.back.home')}
          </Button>
          
          {convertedImage && (
            <Button 
              type="default" 
              icon={<DownloadOutlined />} 
              onClick={() => downloadConvertedImage(convertedImage)}
            >
              {t('compress.download.again')}
            </Button>
          )}
        </div>
        
        {convertedImage && (
          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <Paragraph style={{ color: 'green' }}>
              ✓ {t('detail.conversion.complete')}
            </Paragraph>
            <Image 
              src={convertedImage} 
              alt="Converted" 
              style={{ maxWidth: '100%', maxHeight: '200px', border: `1px solid ${pageColor}` }} 
            />
          </div>
        )}
      </Card>

      {/* Upload Success Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <CheckCircleOutlined style={{ color: pageColor, fontSize: 24 }} />
            <span>{t('detail.upload.status')}</span>
          </div>
        }
        open={uploadModalVisible}
        onOk={() => setUploadModalVisible(false)}
        onCancel={() => setUploadModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setUploadModalVisible(false)}>
            {t('common.close')}
          </Button>,
          <Button 
            key="convert" 
            type="primary" 
            onClick={() => {
              setUploadModalVisible(false);
              handleConvert();
            }}
            style={{ backgroundColor: pageColor, borderColor: pageColor }}
          >
            {t('common.convertNow')}
          </Button>,
        ]}
        centered
        maskClosable={true}
      >
        <div style={{ padding: '10px 0' }}>
          <p><strong>{uploadedFileName}</strong> {t('detail.upload.success')}</p>
          <p>{t('detail.upload.proceed').replace('{fromFormat}', fromFormat.toUpperCase()).replace('{toFormat}', toFormat.toUpperCase())}</p>
        </div>
      </Modal>
    </div>
  );
};

export default DetailPage; 